import { Link } from "react-router-dom";
import {
  IconAgencyMenu,
  IconClientes,
  IconClose,
  IconEdit,
} from "@/assets/images/Icons";
import { Subtarefas } from "./Subtarefas";
import moment from "moment";
import { confirmAlert } from "react-confirm-alert";
import { ServiceResumoDesk } from "@/services/serviceResumoDesk";
import { toast } from "react-toastify";
import { ComentariosTarefa } from "./ComentariosTarefa";
import { formatarValorEmRealBR } from "../../../utils/Utils";
import { IconDelete } from "@/pages/UserGoOn/Desks/ResumoDesk/Icons";
import { useQueryClient } from "@tanstack/react-query";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { buildTaskStatusPayload } from "@/utils/taskStatusPayload";

export function TarefaView({
  task,
  openEdit,
  getTasks,
  setTaskSelected,
  isLoading = false,
  loadingTitle = "",
}: any) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [showExpandButton, setShowExpandButton] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  async function deleteDesk(id: any) {
    confirmAlert({
      title: t("common:confirmation"),
      message: t("tasks.view.delete_confirm"),
      buttons: [
        {
          label: t("common:yes"),
          onClick: async () => {
            try {
              const res = await ServiceResumoDesk.delTasks(id);
              if (res.status === 200) {
                getTasks();
              }
            } catch (error) {
              console.error("Erro:", error);
              toast.error(t("tasks.messages.editError"));
            }
          },
        },
        {
          label: t("common:cancel"),
          onClick: () => { },
        },
      ],
    });
  }

  const handleSetInProgress = async () => {
    if (!task?.id) return;

    try {
      const status = 3; // Status "Em andamento"
      const payload = buildTaskStatusPayload(status, task);
      const res = await ServiceResumoDesk.editTasks(payload, task.id);
      if (res.status === 200) {
        toast.success(t("tasks.view.in_progress_success"));
        getTasks();
        setTaskSelected(null);
        await queryClient.invalidateQueries({ queryKey: ["tarefas"] });
      }
    } catch (error) {
      console.error("Erro ao atualizar tarefa:", error);
    }
  };

  useEffect(() => {
    if (isLoading || !task?.description) {
      setShowExpandButton(false);
      return;
    }

    if (descriptionRef.current) {
      const element = descriptionRef.current;
      setShowExpandButton(element.scrollHeight > element.clientHeight);
    }
  }, [isLoading, task?.description]);

  const toggleDescriptionExpanded = () => {
    setIsDescriptionExpanded(!isDescriptionExpanded);
  };

  const closeTaskView = () => setTaskSelected(null);

  return (
    <>
      <div
        className="w-full h-screen bg-black/5 fixed top-0 right-0"
        onClick={closeTaskView}
      />
      <div className="bg-white max-w-lg w-full p-8 pr-2 rounded-3xl shadow-lg fixed top-20 right-0 sm:right-[24%] z-20 h-full max-h-[80%] animate__animated animate__backInRight animate__faster">
        <div className="overflow-y-auto space-y-3 h-full text-xs">
          {isLoading ? (
            <>
              <div className="flex items-center gap-2 pr-10">
                <span className="h-4 w-4 shrink-0 rounded bg-azul-400" />
                {loadingTitle ? (
                  <h3 className="truncate text-base font-medium text-cinza-800">
                    {loadingTitle}
                  </h3>
                ) : (
                  <div className="h-5 w-2/3 animate-pulse rounded-md bg-cinza-100" />
                )}
              </div>

              <div className="h-7 w-32 animate-pulse rounded-md bg-azul-400/30" />
              <div className="h-11 w-[calc(100%-1rem)] animate-pulse rounded-md bg-cinza-100" />

              <div className="space-y-4 pt-2">
                {[120, 180, 150, 210, 170].map((width, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <span className="mt-0.5 h-4 w-4 shrink-0 animate-pulse rounded-full bg-cinza-100" />
                    <div className="space-y-2">
                      <div className="h-3 w-20 animate-pulse rounded-md bg-cinza-100" />
                      <div
                        className="h-4 animate-pulse rounded-md bg-cinza-100"
                        style={{ width }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-3 pt-3">
                <div className="h-4 w-28 animate-pulse rounded-md bg-cinza-100" />
                <div className="h-10 w-[calc(100%-1rem)] animate-pulse rounded-md bg-cinza-100" />
                <div className="h-4 w-32 animate-pulse rounded-md bg-cinza-100" />
                <div className="flex items-center gap-2">
                  <span className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-cinza-100" />
                  <div className="h-9 flex-1 animate-pulse rounded-md bg-cinza-100" />
                  <div className="h-9 w-10 animate-pulse rounded-lg bg-azul-400/30" />
                </div>
              </div>

              <button
                className="absolute top-0 right-4"
                type="button"
                onClick={closeTaskView}
              >
                <IconClose />
              </button>
            </>
          ) : (
            <>
          <div>
            <div className="flex items-center gap-2">
              <svg
                width="16"
                height="16"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  width="12"
                  height="12"
                  rx="4"
                  fill={`${task.status === 2 ? "#153DCE" : "#F1f1f1"} `}
                />
              </svg>
              <h3
                className={`text-base font-medium m-0 ${
                  task.status === 2 ? "line-through" : ""
                }`}
              >
                {task.title}
              </h3>
            </div>
            {task?.status !== 3 && task?.status !== 2 && (
              <button
                onClick={() => handleSetInProgress()}
                className="text-white bg-azul-400 px-5 py-1 rounded-md hover:bg-azul-400/80 duration-300 mt-2"
              >
                {t("tasks.view.start_activity")}
              </button>
            )}
            <div className="relative">
              <div
                ref={descriptionRef}
                className={`bg-zinc-100 linkColor border my-2 mr-4 border-zinc-200 p-3 rounded-md text-xs overflow-x-hidden transition-all duration-300 ${isDescriptionExpanded ? "h-full" : "max-h-32 overflow-hidden"
                  }`}
                dangerouslySetInnerHTML={{
                  __html:
                    task.description ||
                    `<p>${t("tasks.labels.noObjective")}</p>`,
                }}
              />

              {showExpandButton && (
                <button
                  onClick={toggleDescriptionExpanded}
                  className="absolute -bottom-1 right-6 bg-azul-400 hover:bg-azul-400/80 text-white text-xs px-3 py-1 rounded-full shadow-lg transition-all duration-200 flex items-center gap-1"
                >
                  {isDescriptionExpanded ? (
                    <>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M7 14L12 9L17 14"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {t("tasks.view.collapse")}
                    </>
                  ) : (
                    <>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M7 10L12 15L17 10"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {t("tasks.view.expand")}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
          {task.id_type && (
            <div>
              <div className="flex gap-2 items-center">
                <i className="bi bi-brilliance text-[#797979] text-base"></i>
                <span>{t("tasks.view.type")}:</span>
              </div>
              <span className="pl-6 font-medium">{task?.typeTarefa?.name}</span>
            </div>
          )}
          {task.id_type_desk && (
            <div>
              <div className="flex gap-2 items-center">
                <i className="bi bi-display-fill text-[#797979] text-base"></i>
                <span>{t("tasks.view.project")}:</span>
              </div>
              <span className="pl-6 font-medium">{task?.typeDesk?.name}</span>
            </div>
          )}
          {task.id_type_payment && (
            <div>
              <div className="flex gap-2 items-center">
                <i className="bi bi-cash-coin text-[#797979] text-base"></i>
                <span>{t("tasks.view.paymentType")}:</span>
              </div>
              <span className="pl-6 font-medium">{task.paymentType.name}</span>
            </div>
          )}
          {task.valor_reembolso && (
            <div>
              <div className="flex gap-2 items-center">
                <i className="bi bi-cash-coin text-[#797979] text-base"></i>
                <span>{t("tasks.view.value")}:</span>
              </div>
              <span className="pl-6 font-medium">
                {formatarValorEmRealBR(task?.valor_reembolso)}
              </span>
            </div>
          )}
          {task.id_agency && (
            <div>
              <div className="flex gap-2 items-center">
                <i className="bi bi-buildings-fill text-[#797979] text-base"></i>
                <span>{t("tasks.view.agency")}:</span>
              </div>
              <span className="pl-6 font-medium">
                {task?.agency?.name_fantasy}
              </span>
            </div>
          )}
          {task.id_client && (
            <div>
              <div className="flex gap-2 items-center">
                <i className="bi bi-person-video2 text-[#797979] text-base"></i>
                <span>{t("tasks.view.client")}:</span>
              </div>
              <span className="pl-6 font-medium">
                {task?.cliente?.name_fantasy}
              </span>
            </div>
          )}
          {task.id_protagonista && (
            <div>
              <div className="flex gap-2 items-center">
                <i className="bi bi-person-video text-[#797979] text-base"></i>
                <span>{t("tasks.view.protagonist")}:</span>
              </div>
              <span className="pl-6 font-medium">
                {task?.protagonista?.name} {task?.protagonista?.last_name}
              </span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <i className="bi bi-clock-fill text-[#797979] text-base"></i>
            {moment.utc(task.date).format("DD/MM/YYYY - HH:mm")}
          </div>
          <div>
            <div className="flex gap-2 items-center">
              <i className="bi bi-people-fill text-[#797979] text-base"></i>
              <span>{t("tasks.view.responsible")}:</span>
            </div>
            <span className="pl-6 font-medium">
              {task?.responsavel?.name ?? task?.responsavel}{" "}
              {task?.responsavel?.last_name}
            </span>
          </div>
          <div>
            <div className="flex gap-2 items-center">
              <i className="bi bi-people-fill text-[#797979] text-base"></i>
              <span>{t("tasks.view.organizer")}:</span>
            </div>
            <span className="pl-6 font-medium">
              {task?.user?.name} {task?.user?.last_name}
            </span>
          </div>
          {task.file_id && (
            <div>
              <div className="flex gap-2 items-center">
                <i className="bi bi-file-earmark-text-fill text-[#797979] text-base"></i>
                <span>{t("tasks.view.attachments")}:</span>
              </div>
              <a
                href={task?.file_path?.path}
                target="_blank"
                className="pl-6 font-medium underline text-blue-700"
              >
                {task?.file_path?.name}
              </a>
            </div>
          )}
          {task?.desks && (
            <>
              <div>
                <div className="flex gap-2 items-center">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 13 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.4737 10.9412V9.52942H1.23685C0.891232 9.52942 0.598686 9.40589 0.359211 9.15883C0.119737 8.91177 0 8.60997 0 8.25341V1.27603C0 0.919462 0.119737 0.617649 0.359211 0.370589C0.598686 0.12353 0.891232 0 1.23685 0H11.7631C12.1088 0 12.4013 0.12353 12.6408 0.370589C12.8803 0.617649 13 0.919462 13 1.27603V8.25341C13 8.60997 12.8803 8.91177 12.6408 9.15883C12.4013 9.40589 12.1088 9.52942 11.7631 9.52942H7.5263V10.9412H8.38157C8.52718 10.9412 8.64911 10.9919 8.74735 11.0932C8.8456 11.1946 8.89473 11.3204 8.89473 11.4706C8.89473 11.6208 8.8456 11.7466 8.74735 11.848C8.64911 11.9493 8.52718 12 8.38157 12H4.61843C4.47282 12 4.35089 11.9493 4.25265 11.848C4.1544 11.7466 4.10527 11.6208 4.10527 11.4706C4.10527 11.3204 4.1544 11.1946 4.25265 11.0932C4.35089 10.9919 4.47282 10.9412 4.61843 10.9412H5.4737ZM1.23685 8.47061H11.7631C11.8158 8.47061 11.864 8.44799 11.9079 8.40274C11.9518 8.3575 11.9737 8.30772 11.9737 8.25341V1.27603C11.9737 1.22172 11.9518 1.17194 11.9079 1.12668C11.864 1.08143 11.8158 1.05881 11.7631 1.05881H1.23685C1.18421 1.05881 1.13596 1.08143 1.09209 1.12668C1.04823 1.17194 1.0263 1.22172 1.0263 1.27603V8.25341C1.0263 8.30772 1.04823 8.3575 1.09209 8.40274C1.13596 8.44799 1.18421 8.47061 1.23685 8.47061Z"
                      fill="#797979"
                    />
                  </svg>
                  {task?.desks?.name && (<span>{t("tasks.view.assignedDesk")}:</span>)}
                </div>
                <Link
                  to={`/desks/${task.id_desk}?tab=tarefas`}
                  target="_blank"
                  className="pl-6 font-medium text-azul-400 "
                >
                  <p className="truncate" title={task?.desks?.name}>
                    {task?.desks?.name}
                  </p>
                </Link>
              </div>
              {task?.desks?.status && (
                <div>
                  <div className="flex gap-2 items-center">
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 13 13"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6.5 0.56169C6.5 0.251477 6.75196 -0.00252095 7.06101 0.0242514C8.14871 0.118475 9.19842 0.485546 10.1112 1.09545C11.1801 1.80968 12.0132 2.82484 12.5052 4.01256C12.9972 5.20028 13.1259 6.50721 12.8751 7.76809C12.6609 8.84479 12.1782 9.84661 11.4757 10.6824C11.2761 10.9198 10.9184 10.9184 10.699 10.699C10.4797 10.4797 10.4823 10.1257 10.6776 9.88465C11.2252 9.20873 11.6025 8.40762 11.7733 7.54893C11.9808 6.50596 11.8743 5.42491 11.4674 4.44246C11.0604 3.46001 10.3713 2.62029 9.48709 2.0295C8.75913 1.54309 7.92587 1.24341 7.06069 1.15269C6.75217 1.12034 6.5 0.871902 6.5 0.56169Z"
                        fill="#797979"
                      />
                      <path
                        d="M9.39732 11.6835C9.54867 11.9543 9.45267 12.299 9.16984 12.4264C8.48853 12.7333 7.75807 12.921 7.01034 12.9799C6.07557 13.0536 5.13595 12.9241 4.25594 12.6003C3.37593 12.2766 2.57638 11.7663 1.91215 11.1045C1.38082 10.5751 0.946151 9.95878 0.626155 9.28351C0.493312 9.00319 0.643534 8.6785 0.934282 8.57034C1.22503 8.46219 1.5458 8.61191 1.68368 8.88979C1.94388 9.4142 2.28857 9.89376 2.70505 10.3087C3.25449 10.8562 3.91585 11.2783 4.64377 11.546C5.37169 11.8138 6.14893 11.9209 6.92214 11.86C7.50826 11.8139 8.08154 11.672 8.61955 11.4412C8.90464 11.3189 9.24596 11.4128 9.39732 11.6835Z"
                        fill="#797979"
                      />
                      <path
                        d="M0.599261 7.16694C0.291011 7.20178 0.0103207 6.97995 0.00220895 6.66984C-0.0330779 5.32084 0.352561 3.989 1.11284 2.86289C1.87313 1.73679 2.96428 0.881265 4.22868 0.409744C4.51933 0.301351 4.83001 0.478773 4.91292 0.777701C4.99583 1.07663 4.81922 1.38343 4.53057 1.49705C3.52321 1.89358 2.65453 2.58702 2.04389 3.49149C1.43325 4.39595 1.11479 5.46086 1.12354 6.54342C1.12605 6.85362 0.907511 7.1321 0.599261 7.16694Z"
                        fill="#797979"
                      />
                    </svg>

                    <span>{t("tasks.view.deskStatus")}:</span>
                  </div>
                  <span className="pl-6 font-medium">
                    {task?.desks?.status?.name}
                  </span>
                </div>
              )}
              {task?.desks?.cliente && (
                <div>
                  <div className="flex gap-2 items-center">
                    <IconClientes size={15} />

                    <span>{t("tasks.view.deskClient")}:</span>
                  </div>
                  <span className="pl-6 font-medium">
                    {task?.desks?.cliente?.name_fantasy}
                  </span>
                </div>
              )}
              {task?.desks?.agencia && (
                <div>
                  <div className="flex gap-2 items-center">
                    <IconAgencyMenu size={13} />

                    <span>{t("tasks.view.deskAgency")}:</span>
                  </div>
                  <span className="pl-6 font-medium">
                    {task?.desks?.agencia?.name_fantasy}
                  </span>
                </div>
              )}
            </>
          )}
          {task?.prospecting && (
            <>
              <div>
                <div className="flex gap-2 items-center">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 13 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.4737 10.9412V9.52942H1.23685C0.891232 9.52942 0.598686 9.40589 0.359211 9.15883C0.119737 8.91177 0 8.60997 0 8.25341V1.27603C0 0.919462 0.119737 0.617649 0.359211 0.370589C0.598686 0.12353 0.891232 0 1.23685 0H11.7631C12.1088 0 12.4013 0.12353 12.6408 0.370589C12.8803 0.617649 13 0.919462 13 1.27603V8.25341C13 8.60997 12.8803 8.91177 12.6408 9.15883C12.4013 9.40589 12.1088 9.52942 11.7631 9.52942H7.5263V10.9412H8.38157C8.52718 10.9412 8.64911 10.9919 8.74735 11.0932C8.8456 11.1946 8.89473 11.3204 8.89473 11.4706C8.89473 11.6208 8.8456 11.7466 8.74735 11.848C8.64911 11.9493 8.52718 12 8.38157 12H4.61843C4.47282 12 4.35089 11.9493 4.25265 11.848C4.1544 11.7466 4.10527 11.6208 4.10527 11.4706C4.10527 11.3204 4.1544 11.1946 4.25265 11.0932C4.35089 10.9919 4.47282 10.9412 4.61843 10.9412H5.4737ZM1.23685 8.47061H11.7631C11.8158 8.47061 11.864 8.44799 11.9079 8.40274C11.9518 8.3575 11.9737 8.30772 11.9737 8.25341V1.27603C11.9737 1.22172 11.9518 1.17194 11.9079 1.12668C11.864 1.08143 11.8158 1.05881 11.7631 1.05881H1.23685C1.18421 1.05881 1.13596 1.08143 1.09209 1.12668C1.04823 1.17194 1.0263 1.22172 1.0263 1.27603V8.25341C1.0263 8.30772 1.04823 8.3575 1.09209 8.40274C1.13596 8.44799 1.18421 8.47061 1.23685 8.47061Z"
                      fill="#797979"
                    />
                  </svg>
                  {task?.prospecting?.name && (<span>Atribuída na Prospecção:</span>)}
                </div>
                <div className="pl-6 font-medium pt-1">
                  <p className="truncate" title={task?.prospecting?.name}>
                    {task?.prospecting?.name}
                  </p>
                </div>
              </div>
              {task?.prospecting?.status && (
                <div>
                  <div className="flex gap-2 items-center">
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 13 13"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6.5 0.56169C6.5 0.251477 6.75196 -0.00252095 7.06101 0.0242514C8.14871 0.118475 9.19842 0.485546 10.1112 1.09545C11.1801 1.80968 12.0132 2.82484 12.5052 4.01256C12.9972 5.20028 13.1259 6.50721 12.8751 7.76809C12.6609 8.84479 12.1782 9.84661 11.4757 10.6824C11.2761 10.9198 10.9184 10.9184 10.699 10.699C10.4797 10.4797 10.4823 10.1257 10.6776 9.88465C11.2252 9.20873 11.6025 8.40762 11.7733 7.54893C11.9808 6.50596 11.8743 5.42491 11.4674 4.44246C11.0604 3.46001 10.3713 2.62029 9.48709 2.0295C8.75913 1.54309 7.92587 1.24341 7.06069 1.15269C6.75217 1.12034 6.5 0.871902 6.5 0.56169Z"
                        fill="#797979"
                      />
                      <path
                        d="M9.39732 11.6835C9.54867 11.9543 9.45267 12.299 9.16984 12.4264C8.48853 12.7333 7.75807 12.921 7.01034 12.9799C6.07557 13.0536 5.13595 12.9241 4.25594 12.6003C3.37593 12.2766 2.57638 11.7663 1.91215 11.1045C1.38082 10.5751 0.946151 9.95878 0.626155 9.28351C0.493312 9.00319 0.643534 8.6785 0.934282 8.57034C1.22503 8.46219 1.5458 8.61191 1.68368 8.88979C1.94388 9.4142 2.28857 9.89376 2.70505 10.3087C3.25449 10.8562 3.91585 11.2783 4.64377 11.546C5.37169 11.8138 6.14893 11.9209 6.92214 11.86C7.50826 11.8139 8.08154 11.672 8.61955 11.4412C8.90464 11.3189 9.24596 11.4128 9.39732 11.6835Z"
                        fill="#797979"
                      />
                      <path
                        d="M0.599261 7.16694C0.291011 7.20178 0.0103207 6.97995 0.00220895 6.66984C-0.0330779 5.32084 0.352561 3.989 1.11284 2.86289C1.87313 1.73679 2.96428 0.881265 4.22868 0.409744C4.51933 0.301351 4.83001 0.478773 4.91292 0.777701C4.99583 1.07663 4.81922 1.38343 4.53057 1.49705C3.52321 1.89358 2.65453 2.58702 2.04389 3.49149C1.43325 4.39595 1.11479 5.46086 1.12354 6.54342C1.12605 6.85362 0.907511 7.1321 0.599261 7.16694Z"
                        fill="#797979"
                      />
                    </svg>

                    <span>Status da Prospecção:</span>
                  </div>
                  <span className="pl-6 font-medium">
                    {task?.prospecting?.status?.name}
                  </span>
                </div>
              )}
              {task?.prospecting?.cliente && (
                <div>
                  <div className="flex gap-2 items-center">
                    <IconClientes size={15} />

                    <span>Cliente da Prospecção:</span>
                  </div>
                  <span className="pl-6 font-medium">
                    {task?.prospecting?.cliente?.name_fantasy}
                  </span>
                </div>
              )}
              {task?.prospecting?.agencia && (
                <div>
                  <div className="flex gap-2 items-center">
                    <IconAgencyMenu size={13} />

                    <span>Agência da Prospecção:</span>
                  </div>
                  <span className="pl-6 font-medium">
                    {task?.prospecting?.agencia?.name_fantasy ?? task?.prospecting?.agencia?.nome}
                  </span>
                </div>
              )}
            </>
          )}
          {task.reuniao && (
            <>
              <div>
                <div className="flex gap-2 items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="#797979"
                    viewBox="0 0 16 16"
                  >
                    <path d="M8.447.276a.5.5 0 0 0-.894 0L7.19 1H2.5A1.5 1.5 0 0 0 1 2.5V10h14V2.5A1.5 1.5 0 0 0 13.5 1H8.809z" />
                    <path
                      fillRule="evenodd"
                      d="M.5 11a.5.5 0 0 0 0 1h2.86l-.845 3.379a.5.5 0 0 0 .97.242L3.89 14h8.22l.405 1.621a.5.5 0 0 0 .97-.242L12.64 12h2.86a.5.5 0 0 0 0-1zm3.64 2 .25-1h7.22l.25 1z"
                    />
                  </svg>
                  <span>{t("tasks.view.assignedMeeting")}:</span>
                </div>
                <span className="pl-6 font-medium">{task.reuniao.title}</span>
              </div>
              <div>
                <div className="flex gap-2 items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="#797979"
                    viewBox="0 0 16 16"
                  >
                    <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5M2 2a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z" />
                    <path d="M2.5 4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5H3a.5.5 0 0 1-.5-.5zM11 7.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm-3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm-5 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5z" />
                  </svg>
                  <span>{t("tasks.view.meetingDate")}:</span>
                </div>
                <span className="pl-6 font-medium">
                  {moment(task.reuniao.date).format("DD/MM/YYYY - HH:mm")}
                </span>
              </div>
              <div>
                <div className="flex gap-2 items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="#797979"
                    viewBox="0 0 16 16"
                  >
                    <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6" />
                  </svg>
                  <span>{t("tasks.view.meetingProtagonist")}:</span>
                </div>
                <span className="pl-6 font-medium">
                  {task.reuniao?.protagonista?.name}{" "}
                  {task.reuniao?.protagonista?.last_name}
                </span>
              </div>
              <div>
                <div className="flex gap-2 items-center">
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 13 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6.5 0.56169C6.5 0.251477 6.75196 -0.00252095 7.06101 0.0242514C8.14871 0.118475 9.19842 0.485546 10.1112 1.09545C11.1801 1.80968 12.0132 2.82484 12.5052 4.01256C12.9972 5.20028 13.1259 6.50721 12.8751 7.76809C12.6609 8.84479 12.1782 9.84661 11.4757 10.6824C11.2761 10.9198 10.9184 10.9184 10.699 10.699C10.4797 10.4797 10.4823 10.1257 10.6776 9.88465C11.2252 9.20873 11.6025 8.40762 11.7733 7.54893C11.9808 6.50596 11.8743 5.42491 11.4674 4.44246C11.0604 3.46001 10.3713 2.62029 9.48709 2.0295C8.75913 1.54309 7.92587 1.24341 7.06069 1.15269C6.75217 1.12034 6.5 0.871902 6.5 0.56169Z"
                      fill="#797979"
                    />
                    <path
                      d="M9.39732 11.6835C9.54867 11.9543 9.45267 12.299 9.16984 12.4264C8.48853 12.7333 7.75807 12.921 7.01034 12.9799C6.07557 13.0536 5.13595 12.9241 4.25594 12.6003C3.37593 12.2766 2.57638 11.7663 1.91215 11.1045C1.38082 10.5751 0.946151 9.95878 0.626155 9.28351C0.493312 9.00319 0.643534 8.6785 0.934282 8.57034C1.22503 8.46219 1.5458 8.61191 1.68368 8.88979C1.94388 9.4142 2.28857 9.89376 2.70505 10.3087C3.25449 10.8562 3.91585 11.2783 4.64377 11.546C5.37169 11.8138 6.14893 11.9209 6.92214 11.86C7.50826 11.8139 8.08154 11.672 8.61955 11.4412C8.90464 11.3189 9.24596 11.4128 9.39732 11.6835Z"
                      fill="#797979"
                    />
                    <path
                      d="M0.599261 7.16694C0.291011 7.20178 0.0103207 6.97995 0.00220895 6.66984C-0.0330779 5.32084 0.352561 3.989 1.11284 2.86289C1.87313 1.73679 2.96428 0.881265 4.22868 0.409744C4.51933 0.301351 4.83001 0.478773 4.91292 0.777701C4.99583 1.07663 4.81922 1.38343 4.53057 1.49705C3.52321 1.89358 2.65453 2.58702 2.04389 3.49149C1.43325 4.39595 1.11479 5.46086 1.12354 6.54342C1.12605 6.85362 0.907511 7.1321 0.599261 7.16694Z"
                      fill="#797979"
                    />
                  </svg>

                  <span>{t("tasks.view.meetingStatus")}:</span>
                </div>
                <span className="pl-6 font-medium">
                  {task.reuniao.status == 1 &&
                    t("tasks.view.meetingStatusScheduled")}
                  {task.reuniao.status == 2 &&
                    t("tasks.view.meetingStatusDone")}
                  {task.reuniao.status == 3 &&
                    t("tasks.view.meetingStatusReschedule")}
                </span>
              </div>
            </>
          )}
          <Subtarefas data={task} />
          <ComentariosTarefa data={task} parentId={task.id} type={"tarefa"} />
          <div className="flex items-center justify-end gap-3 bg-azul-400 w-fit rounded-xl px-3 py-1 absolute bottom-8 right-8">
            <button
              type="button"
              className="text-white"
              onClick={() => openEdit(task)}
            >
              <IconEdit />
            </button>
            <button
              type="button"
              className="text-white hover:text-red-500 duration-300"
              onClick={() => deleteDesk(task.id)}
            >
              <IconDelete />
            </button>
          </div>

          <button
            className="absolute top-0 right-4"
            type="button"
            onClick={closeTaskView}
          >
            <IconClose />
          </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
